import React, { useEffect, useMemo, useState } from 'react';
import Big from 'big.js';
import cx from 'classnames';
import { useMutation, useQuery } from '@apollo/client';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  FaArrowLeft,
  FaBox,
  FaChevronDown,
  FaChevronUp,
  FaEdit,
  FaPlus,
  FaSave,
  FaTrash,
} from 'react-icons/fa';
import {
  CREATE_RESTOCK_SHIPMENT_CARTON_BOX,
  GET_RESTOCK_SHIPMENT,
  CREATE_RESTOCK_SHIPMENT,
  UPDATE_RESTOCK_SHIPMENT,
  CREATE_RESTOCK_SHIPMENT_PRODUCT,
  DELETE_RESTOCK_SHIPMENT_PRODUCT,
  UPDATE_RESTOCK_SHIPMENT_PRODUCT_VARIATION,
  DELETE_RESTOCK_SHIPMENT_PRODUCT_VARIATION,
  DELETE_RESTOCK_SHIPMENT_CARTON_BOX,
  UPDATE_RESTOCK_SHIPMENT_CARTON_BOX,
  UNLINK_CARTON_BOX_FROM_VARIATION,
} from './RestockShipments.queries';
import Button from '../common/Button';
import { Screen } from '../Screen';
import { TextInput } from '../common/TextInput';
import { useRequestManager } from '../common/RequestManager';
import AddProductModal from './Restock_AddProductModal';
import AddVariationModal from './Restock_AddProductVariationModal';
import AddCartonBoxModal from './Restock_AddCartonBoxModal';
import LinkVariationToBoxModal from './LinkVariationToBoxModal';
import dayjs from 'dayjs';
import {
  GetRestockShipmentQuery,
  GetRestockShipmentQueryVariables,
} from '../../../codegen/graphql';

const RestockShipmentFormPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { trackRequest } = useRequestManager();
  const isEditMode = !!id;
  const [activeTab, setActiveTab] = useState<'products' | 'boxes'>(
    location.pathname.endsWith('/boxes') ? 'boxes' : 'products'
  );
  const [isShipmentInfoExpanded, setIsShipmentInfoExpanded] = useState(false);

  const [isAddProductModalOpen, setIsAddProductModalOpen] = useState(false);
  const [isAddBoxModalOpen, setIsAddBoxModalOpen] = useState(false);
  const [addVariationProduct, setAddVariationProduct] = useState<{
    id: string;
    restockShipmentId: string;
    supplierProductId: string;
  } | null>(null);
  const [editingBoxId, setEditingBoxId] = useState<string | null>(null);
  const [linkModalState, setLinkModalState] = useState<{
    boxId: string;
    editingVariation?: any;
    editingLink?: any;
  } | null>(null);

  const [formData, setFormData] = useState({
    shipName: '',
    title: '',
    deliveryAddress: '',
    freightCost: '',
    freightCostCurrency: 'USD',
  });

  const { data: shipmentData, refetch, loading } = useQuery<
    GetRestockShipmentQuery,
    GetRestockShipmentQueryVariables
  >(GET_RESTOCK_SHIPMENT, {
    variables: { id: id || '' },
    skip: !isEditMode,
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      if (data?.restockShipment) {
        const shipment = data.restockShipment;
        setFormData({
          shipName: shipment.shipName || '',
          title: shipment.title || '',
          deliveryAddress: shipment.deliveryAddress || '',
          freightCost: shipment.freightCost?.toString() || '',
          freightCostCurrency: shipment.freightCostCurrency || 'USD',
        });
      }
    },
  });

  const [createShipment] = useMutation(CREATE_RESTOCK_SHIPMENT);
  const [updateShipment] = useMutation(UPDATE_RESTOCK_SHIPMENT);
  const [createProduct] = useMutation(CREATE_RESTOCK_SHIPMENT_PRODUCT);
  const [deleteProduct] = useMutation(DELETE_RESTOCK_SHIPMENT_PRODUCT);
  const [updateVariation] = useMutation(UPDATE_RESTOCK_SHIPMENT_PRODUCT_VARIATION);
  const [deleteVariation] = useMutation(DELETE_RESTOCK_SHIPMENT_PRODUCT_VARIATION);
  const [createCartonBox] = useMutation(CREATE_RESTOCK_SHIPMENT_CARTON_BOX);
  const [deleteCartonBox] = useMutation(DELETE_RESTOCK_SHIPMENT_CARTON_BOX);
  const [updateCartonBox] = useMutation(UPDATE_RESTOCK_SHIPMENT_CARTON_BOX);
  const [unlinkVariation] = useMutation(UNLINK_CARTON_BOX_FROM_VARIATION);

  useEffect(() => {
    setActiveTab(location.pathname.endsWith('/boxes') ? 'boxes' : 'products');
  }, [location.pathname]);

  const shipment = shipmentData?.restockShipment;
  const products = shipment?.products || [];
  const cartonBoxes = shipment?.cartonBoxes || [];
  const totalInventoryAmount = shipment?.inventory?.total?.amount || 0;
  const soldInventoryAmount = shipment?.inventory?.sold?.amount || 0;
  const blockedInventoryAmount = new Big(totalInventoryAmount).minus(soldInventoryAmount).toNumber();
  const totalInventoryQty = shipment?.inventory?.total?.quantity || 0;
  const soldInventoryQty = shipment?.inventory?.sold?.quantity || 0;
  const blockedInventoryQty = Math.max(totalInventoryQty - soldInventoryQty, 0);
  const soldPercent = totalInventoryAmount
    ? Math.round(new Big(soldInventoryAmount).div(totalInventoryAmount).mul(100).toNumber())
    : 0;
  const formatAmount = (value: number) => Math.round(value).toLocaleString();
  const formatCount = (value: number) => Math.round(value).toLocaleString();

  const calculateReceivedQuantity = (variationId: string) => {
    return cartonBoxes.reduce((acc, box) => {
      const inBox = (box.linkedProductVariations || []).reduce((boxAcc, link) => {
        if (link.restockShipmentProductVariationId === variationId) {
          return boxAcc + (link.quantity || 0);
        }
        return boxAcc;
      }, 0);
      return acc + inBox;
    }, 0);
  };

  const allVariations = useMemo(() => {
    return products.flatMap((product) =>
      (product.variations || []).map((variation) => ({
        productName: product.supplierProduct?.name['FR'] || 'Unknown Product',
        variation,
      }))
    );
  }, [products]);

  const verificationStats = useMemo(() => {
    const total = allVariations.length;
    const complete = allVariations.filter(
      ({ variation }) => calculateReceivedQuantity(variation.id) >= variation.quantity
    ).length;
    return { total, complete, missing: Math.max(total - complete, 0) };
  }, [allVariations, cartonBoxes]);

  const getVariationLabel = (variation: any) =>
    variation.supplierProductVariation?.typeValues?.map((tv: any) => tv.value['FR']).join(' / ') ||
    'No variation info';

  const findVariationById = (variationId: string) => {
    for (const product of products) {
      const variation = (product.variations || []).find((item) => item.id === variationId);
      if (variation) {
        return {
          productName: product.supplierProduct?.name['FR'] || 'Unknown Product',
          variation,
        };
      }
    }
    return null;
  };

  const handleSaveShipment = async () => {
    const input = {
      shipName: formData.shipName || null,
      title: formData.title || null,
      deliveryAddress: formData.deliveryAddress || null,
      freightCost: formData.freightCost ? parseFloat(formData.freightCost) : null,
      freightCostCurrency: formData.freightCostCurrency || null,
    };

    if (isEditMode) {
      await trackRequest(
        updateShipment({
          variables: { id, input },
        }),
        { title: 'Updating shipment' }
      );
    } else {
      const result = await trackRequest(
        createShipment({
          variables: { input },
        }),
        { title: 'Creating shipment' }
      );
      
      if (result?.data?.createRestockShipment) {
        navigate(`/restock-shipments/${result.data.createRestockShipment.id}/edit`);
      }
    }
  };

  const handleAddProduct = async (supplierProductId: string) => {
    if (!id) {
      // Need to create shipment first
      const result = await trackRequest(
        createShipment({
          variables: {
            input: {
              shipName: formData.shipName || null,
              title: formData.title || null,
              deliveryAddress: formData.deliveryAddress || null,
              freightCost: formData.freightCost ? parseFloat(formData.freightCost) : null,
              freightCostCurrency: formData.freightCostCurrency || null,
            },
          },
        }),
        { title: 'Creating shipment' }
      );

      if (result?.data?.createRestockShipment) {
        const newShipmentId = result.data.createRestockShipment.id;
        await trackRequest(
          createProduct({
            variables: {
              input: {
                restockShipmentId: newShipmentId,
                supplierProductId,
              },
            },
          }),
          { title: 'Adding product' }
        );
        navigate(`/restock-shipments/${newShipmentId}/edit`);
        refetch();
      }
    } else {
      await trackRequest(
        createProduct({
          variables: {
            input: {
              restockShipmentId: id,
              supplierProductId,
            },
          },
        }),
        { title: 'Adding product' }
      );
      refetch();
    }
    setIsAddProductModalOpen(false);
  };

  const handleDeleteProduct = async (productId: string) => {
    if (confirm('Are you sure you want to remove this product?')) {
      await trackRequest(
        deleteProduct({
          variables: { id: productId },
        }),
        { title: 'Removing product' }
      );
      refetch();
    }
  };

  const handleDeleteVariation = async (variationId: string) => {
    if (confirm('Are you sure you want to remove this variation?')) {
      await trackRequest(
        deleteVariation({
          variables: { id: variationId },
        }),
        { title: 'Removing variation' }
      );
      refetch();
    }
  };

  const handleUpdateVariationQuantity = async (variationId: string, value: string) => {
    const parsedValue = parseInt(value, 10);
    if (Number.isNaN(parsedValue)) return;
    await trackRequest(
      updateVariation({
        variables: {
          id: variationId,
          input: {
            quantity: parsedValue,
          },
        },
      }),
      { title: 'Updating variation quantity' }
    );
    refetch();
  };

  const handleUpdateVariationCost = async (variationId: string, value: string) => {
    const parsedValue = parseFloat(value);
    if (Number.isNaN(parsedValue)) return;
    await trackRequest(
      updateVariation({
        variables: {
          id: variationId,
          input: {
            totalCost: parsedValue,
          },
        },
      }),
      { title: 'Updating variation cost' }
    );
    refetch();
  };

  const handleAddCartonBox = async (boxData: any) => {
    if (!id) return;
    const result = await trackRequest(
      createCartonBox({
        variables: {
          input: {
            restockShipmentId: id,
            ...boxData,
          },
        },
      }),
      { title: 'Adding carton box' }
    );
    const newBoxId = result?.data?.createRestockShipmentCartonBox?.id;
    refetch();
    setIsAddBoxModalOpen(false);
    if (newBoxId) {
      setLinkModalState({ boxId: newBoxId });
    }
  };

  const handleDeleteCartonBox = async (boxId: string) => {
    if (confirm('Are you sure you want to delete this carton box?')) {
      await trackRequest(
        deleteCartonBox({
          variables: { id: boxId },
        }),
        { title: 'Deleting carton box' }
      );
      refetch();
    }
  };

  const handleUpdateBoxField = async (boxId: string, field: string, value: any) => {
    await trackRequest(
      updateCartonBox({
        variables: {
          id: boxId,
          input: {
            [field]: value,
          },
        },
      }),
      { title: 'Updating carton box' }
    );
    refetch();
  };

  const handleUnlinkVariation = async (boxId: string, variationId: string) => {
    if (confirm('Are you sure you want to unlink this variation from this box?')) {
      await trackRequest(
        unlinkVariation({
          variables: {
            cartonBoxId: boxId,
            variationId,
          },
        }),
        { title: 'Unlinking variation from box' }
      );
      refetch();
    }
  };

  if (isEditMode && loading && !shipment) {
    return (
      <Screen>
        <div className="container mx-auto p-6">
          <div className="text-center py-8 text-gray-500">Loading shipment...</div>
        </div>
      </Screen>
    );
  }

  return (
    <Screen>
      <div className="container mx-auto p-6 pb-24">
        <div className="flex items-center mb-6">
          <Button
            preset="white"
            onClick={() => navigate('/restock-shipments')}
            className="mr-4"
            size="sm"
          >
            <FaArrowLeft />
            Back
          </Button>
          <h1 className="text-2xl font-bold text-gray-800">
            {isEditMode ? formData.title || 'Edit Restock Shipment' : 'New Restock Shipment'}
          </h1>
        </div>

        <div className="bg-white rounded-xl shadow-sm mb-6 overflow-hidden">
          {isEditMode && shipment ? (
            <div className="px-4 py-4 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4 border-b border-gray-100">
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                <p className="text-xs uppercase tracking-wide text-gray-500">Shipment</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {formData.title || formData.shipName || 'Unnamed'}
                </p>
                <p className="text-xl text-blue-600">
                  {formatAmount(shipment.totalWeight || 0)} kg
                </p>
                <p className="text-lg text-gray-600 mt-1">
                  {formatCount(products.length)} products | {formatCount(cartonBoxes.length)} boxes
                </p>
                <p className="text-lg text-black/80 mt-2">
                  Freight: {formatAmount(Number(formData.freightCost || 0))} {formData.freightCostCurrency
                  }<br/>
                  {new Big(formData.freightCost || 0).div(shipment.totalWeight || 1).toFixed(2)} {formData.freightCostCurrency} per kg
                </p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                <p className="text-xs uppercase tracking-wide text-gray-500">Total Inventory</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {formatAmount(totalInventoryAmount)} EUR
                </p>
                <p className="text-lg text-gray-600 mt-1">{formatCount(totalInventoryQty)} units</p>
                <p className="text-lg text-black/80 mt-2">
                  Total product cost: {new Big(totalInventoryAmount).minus(formData.freightCost || 0).toFixed(0)} {formData.freightCostCurrency}
                </p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                <p className="text-xs uppercase tracking-wide text-gray-500">Sold Inventory</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {formatAmount(soldInventoryAmount)} EUR
                </p>
                <p className="text-lg text-gray-600 mt-1">{formatCount(soldInventoryQty)} units</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                <p className="text-xs uppercase tracking-wide text-gray-500">Blocked Inventory</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {formatAmount(blockedInventoryAmount)} EUR
                </p>
                <p className="text-lg text-gray-600 mt-1">{formatCount(blockedInventoryQty)} units</p>
              </div>
              <div className="bg-yellow-300 rounded-xl border border-yellow-400 shadow-sm p-4">
                <p className="text-xs uppercase tracking-wide text-black">Sold Rate</p>
                <p className="text-5xl font-bold text-black leading-none mt-1">{soldPercent}%</p>
                
              </div>
            </div>
          ) : null}
          <div className="px-4 py-3 flex flex-wrap gap-3 items-center justify-end">
            <Button
              preset="white"
              size="sm"
              onClick={() => setIsShipmentInfoExpanded((prev) => !prev)}
            >
              {isShipmentInfoExpanded ? <FaChevronUp /> : <FaChevronDown />}
              {isShipmentInfoExpanded ? 'Hide details' : 'Show details'}
            </Button>
          </div>
          {isShipmentInfoExpanded && (
            <div className="p-4 border-t border-blue-100">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ship Name</label>
                  <TextInput
                    value={formData.shipName}
                    onChange={(e) => setFormData({ ...formData, shipName: e.target.value })}
                    placeholder="Enter ship name"
                  />
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Freight Cost
                    </label>
                    <TextInput
                      type="number"
                      value={formData.freightCost}
                      onChange={(e) => setFormData({ ...formData, freightCost: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="w-24">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                    <select
                      value={formData.freightCostCurrency}
                      onChange={(e) =>
                        setFormData({ ...formData, freightCostCurrency: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                      <option value="CNY">CNY</option>
                    </select>
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <TextInput
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Enter title"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Delivery Address
                  </label>
                  <textarea
                    value={formData.deliveryAddress}
                    onChange={(e) => setFormData({ ...formData, deliveryAddress: e.target.value })}
                    placeholder="Enter delivery address"
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="mt-4">
                <Button preset="primary" onClick={handleSaveShipment}>
                  <FaSave />
                  {isEditMode ? 'Update Shipment' : 'Create Shipment'}
                </Button>
              </div>
            </div>
          )}
        </div>

        {isEditMode && (
          <>
            <div className="sticky top-4 bg-white border border-gray-200 rounded-xl shadow-sm mb-4 p-2 inline-flex gap-2">
              <Button
                preset={activeTab === 'products' ? 'primary' : 'white'}
                size="sm"
                onClick={() => setActiveTab('products')}
              >
                Products
              </Button>
              <Button
                preset={activeTab === 'boxes' ? 'primary' : 'white'}
                size="sm"
                onClick={() => setActiveTab('boxes')}
              >
                Carton Boxes
              </Button>
            </div>

            {activeTab === 'products' && (
              <div className="bg-white border border-green-100 rounded-xl shadow-sm overflow-hidden">
                <div className="px-4 py-3">
                  <h2 className="text-lg font-semibold">Products</h2>
                  <p className="text-sm mt-1">
                    Quantity verification: {verificationStats.complete}/{verificationStats.total} variations complete, {verificationStats.missing} pending.
                  </p>
                </div>

                {products.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No products added yet. Use Add Product to get started.
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {products.map((product) => (
                      <div key={product.id}>
                        <div className="px-4 py-3 bg-gray-200 flex items-center justify-between">
                          <div>
                            <p className="font-semibold text-gray-800 text-lg">
                              {product.supplierProduct?.name['FR'] || 'Unknown Product'}
                            </p>
                            <p className="text-base text-gray-500">
                              {(product.variations || []).reduce((acc, variation) => (
                                acc + (variation.cost?.totalProductCost ?? 0)
                              ), 0)} {formData.freightCostCurrency} / {' '}

                              {(product.variations || []).reduce((acc, variation) => (
                                acc + (variation.quantity ?? 0)
                              ), 0)} units
                            </p>
                            {/* <p className="text-xs text-gray-500">
                              {product.supplierProduct?.supplierName || 'Unknown Supplier'}
                            </p> */}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              preset="white"
                              onClick={() =>
                                setAddVariationProduct({
                                  id: product.id,
                                  restockShipmentId: product.restockShipmentId,
                                  supplierProductId: product.supplierProductId,
                                })
                              }
                            >
                              <FaPlus />
                              Variation
                            </Button>
                            <Button
                              size="sm"
                              preset="white"
                              color="red"
                              onClick={() => handleDeleteProduct(product.id)}
                            >
                              <FaTrash />
                              Remove
                            </Button>
                          </div>
                        </div>
                        <div
                          className={cx(
                            "px-4 py-2 text-xs grid grid-cols-12 gap-3",
                            product.variations?.length === 0 ? 'hidden' : '',
                            product.variations?.some((variation) => calculateReceivedQuantity(variation.id) != variation.quantity) ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600',
                          )}
                        >
                          <span className="col-span-4">Variation</span>
                          <span className="col-span-2">Cost</span>
                          <span className="col-span-2">Quantity</span>
                          <span className="col-span-1">Found</span>
                          <span className="col-span-2">Status</span>
                          <span className="col-span-1 text-right">Action</span>
                        </div>
                        {(product.variations || []).length === 0 ? (
                          <div className="px-8 py-3 text-sm text-gray-500">
                            No variations yet.
                          </div>
                        ) : (
                          <div className="divide-y divide-green-50">
                            {(product.variations || []).map((variation) => {
                              const foundQuantity = calculateReceivedQuantity(variation.id);
                              const hasMissing = foundQuantity != variation.quantity;
                              return (
                                <div
                                  key={variation.id}
                                  className="px-8 py-3 grid grid-cols-12 gap-3 items-start"
                                >
                                  <div className="col-span-4 text-base text-gray-700 flex items-start flex-col gap-2">
                                    <p className="font-semibold">{getVariationLabel(variation)}</p>
                                    <p>
                                      <span className="text-base text-blue-600">
                                        Unit:{' '}
                                        {variation.cost?.unitCost != null
                                          ? Number(variation.cost.unitCost).toFixed(2)
                                          : '-'}{' '}
                                        {variation.totalCostCurrency || ''}
                                      </span>{' | '}
                                      <span className="text-base text-gray-600">
                                        Freight: {' '}
                                        {(new Big(variation.cost?.totalFreightCost ?? 0).div(variation.quantity)).toFixed(2)}{' '}
                                        {variation.totalCostCurrency || ''}
                                      </span>{' | '}
                                      <span className="text-base text-gray-600">
                                        Product: {' '}
                                        {new Big(variation.cost?.totalProductCost ?? 0).div(variation.quantity).toFixed(2)}{' '}
                                        {variation.totalCostCurrency || ''}
                                      </span>
                                    </p>
                                  </div>
                                  <div className="col-span-2">
                                    <TextInput
                                      type="number"
                                      defaultValue={variation.totalCost ?? undefined}
                                      onBlur={(e) =>
                                        handleUpdateVariationCost(variation.id, e.target.value)
                                      }
                                      size="xs"
                                    />
                                    <p className="text-[10px] text-gray-500 mt-1">
                                      {variation.totalCostCurrency || ''}
                                    </p>
                                  </div>
                                  <div className="col-span-2">
                                    <TextInput
                                      type="number"
                                      defaultValue={variation.quantity}
                                      onBlur={(e) =>
                                        handleUpdateVariationQuantity(variation.id, e.target.value)
                                      }
                                      size="xs"
                                    />
                                  </div>
                                  <div className="col-span-1 text-sm font-medium text-gray-800">
                                    {foundQuantity}
                                  </div>
                                  <div className="col-span-2">
                                    <span
                                      className={`text-xs px-2 py-1 rounded ${
                                        hasMissing
                                          ? 'bg-amber-100 text-amber-700'
                                          : 'bg-green-100 text-green-700'
                                      }`}
                                    >
                                      {hasMissing ? 'Missing qty' : 'Matched'}
                                    </span>
                                  </div>
                                  <div className="col-span-1 flex justify-end">
                                    <Button
                                      size="sm"
                                      preset="white"
                                      color="red"
                                      onClick={() => handleDeleteVariation(variation.id)}
                                    >
                                      <FaTrash />
                                    </Button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'boxes' && (
              <div className="bg-white border border-blue-100 rounded-xl shadow-sm overflow-hidden">
                <div className="px-4 py-3 flex justify-between items-center">
                  <h2 className="text-lg font-semibold text-blue-900">
                    Carton Boxes ({cartonBoxes.length})
                  </h2>
                </div>

                {cartonBoxes.length === 0 ? (
                  <div className="py-8 text-cente r text-gray-500">No carton boxes added yet.</div>
                ) : (
                  <div className="divide-y divide-blue-50">
                    {cartonBoxes.map((box) => {
                      const isEditing = editingBoxId === box.id;
                      const boxFillCoeffSum = (box.linkedProductVariations || []).reduce(
                        (acc, link) => acc + (link.boxFillCoeff || 0),
                        0
                      );
                      const hasInvalidBoxFillCoeffSum =
                        (box.linkedProductVariations || []).length > 0 &&
                        Math.abs(boxFillCoeffSum - 1) > 0.0001;
                      return (
                        <div key={box.id} className="px-4 py-3">
                          <div className="grid grid-cols-12 gap-3 items-center text-sm">
                            <div className="col-span-2 font-semibold text-gray-800">
                              <FaBox className="inline text-blue-500 mr-1" />
                              Box #{box.id.slice(0, 8)}
                            </div>
                            <div className="col-span-3">
                              {isEditing ? (
                                <div className="grid grid-cols-3 gap-1">
                                  <input
                                    type="number"
                                    defaultValue={box.size1 || undefined}
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                                    placeholder="L"
                                    onBlur={(e) =>
                                      handleUpdateBoxField(
                                        box.id,
                                        'size1',
                                        e.target.value ? parseFloat(e.target.value) : null
                                      )
                                    }
                                  />
                                  <input
                                    type="number"
                                    defaultValue={box.size2 || undefined}
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                                    placeholder="W"
                                    onBlur={(e) =>
                                      handleUpdateBoxField(
                                        box.id,
                                        'size2',
                                        e.target.value ? parseFloat(e.target.value) : null
                                      )
                                    }
                                  />
                                  <input
                                    type="number"
                                    defaultValue={box.size3 || undefined}
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                                    placeholder="H"
                                    onBlur={(e) =>
                                      handleUpdateBoxField(
                                        box.id,
                                        'size3',
                                        e.target.value ? parseFloat(e.target.value) : null
                                      )
                                    }
                                  />
                                </div>
                              ) : (
                                <span>
                                  {box.size1 || '?'} x {box.size2 || '?'} x {box.size3 || '?'} cm
                                </span>
                              )}
                            </div>
                            <div className="col-span-2">
                              {isEditing ? (
                                <input
                                  type="number"
                                  defaultValue={box.weight || undefined}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                                  placeholder="Weight"
                                  onBlur={(e) =>
                                    handleUpdateBoxField(
                                      box.id,
                                      'weight',
                                      e.target.value ? parseFloat(e.target.value) : null
                                    )
                                  }
                                />
                              ) : (
                                <span>
                                  {box.finalWeight.weight} kg ({box.finalWeight.type})
                                </span>
                              )}
                            </div>
                            <div className="col-span-2">
                              {isEditing ? (
                                <input
                                  type="datetime-local"
                                  defaultValue={
                                    box.deliveredAt
                                      ? dayjs(box.deliveredAt).format('YYYY-MM-DDTHH:mm')
                                      : ''
                                  }
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                                  onBlur={(e) =>
                                    handleUpdateBoxField(
                                      box.id,
                                      'deliveredAt',
                                      e.target.value || null
                                    )
                                  }
                                />
                              ) : (
                                <span className="text-gray-600">
                                  {box.deliveredAt
                                    ? dayjs(box.deliveredAt).format('MMM DD, YYYY HH:mm')
                                    : 'Not delivered'}
                                </span>
                              )}
                            </div>
                            <div className="col-span-3">
                              {isEditing ? (
                                <input
                                  type="text"
                                  defaultValue={box.trackingNumber || ''}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                                  placeholder="Tracking number"
                                  onBlur={(e) =>
                                    handleUpdateBoxField(
                                      box.id,
                                      'trackingNumber',
                                      e.target.value || null
                                    )
                                  }
                                />
                              ) : (
                                <span className="text-gray-600">
                                  Tracking: {box.trackingNumber || 'N/A'}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="mt-2 flex justify-between items-center">
                            <div className="text-base text-blue-700">
                              Freight cost: {box.freightCost || 0} {shipment?.freightCostCurrency || ''} | Fill coeff sum:{' '}
                              {boxFillCoeffSum.toFixed(2)}
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                preset="white"
                                onClick={() => setEditingBoxId(isEditing ? null : box.id)}
                              >
                                <FaEdit />
                                {isEditing ? 'Done' : 'Edit'}
                              </Button>
                              <Button
                                size="sm"
                                preset="white"
                                onClick={() => setLinkModalState({ boxId: box.id })}
                              >
                                <FaPlus />
                                Link Variation
                              </Button>
                              <Button
                                size="sm"
                                preset="white"
                                color="red"
                                onClick={() => handleDeleteCartonBox(box.id)}
                              >
                                <FaTrash />
                                Delete
                              </Button>
                            </div>
                          </div>

                          <div className="mt-2 ml-6 border-l-2 border-blue-100 pl-3 space-y-1">
                            {(box.linkedProductVariations || []).length === 0 ? (
                              <p className="text-xs text-gray-500 py-1">No linked variations.</p>
                            ) : (
                              (box.linkedProductVariations || []).map((link) => {
                                const found = findVariationById(link.restockShipmentProductVariationId);
                                if (!found) return null;
                                return (
                                  <div
                                    key={`${box.id}-${found.variation.id}`}
                                    className={cx(
                                      'flex justify-between items-center rounded px-2 py-1 text-xs',
                                      hasInvalidBoxFillCoeffSum ? 'bg-yellow-100' : 'bg-blue-50/70'
                                    )}
                                  >
                                    <div>
                                      <span className="font-medium">{found.productName}</span> -{' '}
                                      {getVariationLabel(found.variation)} | Qty: {link.quantity} | Fill coeff:{' '}
                                      {link.boxFillCoeff ?? 0}
                                    </div>
                                    <div className="flex gap-1">
                                      <Button
                                        size="sm"
                                        preset="white"
                                        onClick={() =>
                                          setLinkModalState({
                                            boxId: box.id,
                                            editingVariation: found.variation,
                                            editingLink: link,
                                          })
                                        }
                                      >
                                        <FaEdit />
                                      </Button>
                                      <Button
                                        size="sm"
                                        preset="white"
                                        color="red"
                                        onClick={() =>
                                          handleUnlinkVariation(
                                            box.id,
                                            link.restockShipmentProductVariationId
                                          )
                                        }
                                      >
                                        <FaTrash />
                                      </Button>
                                    </div>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {!isEditMode && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-blue-800">
            <p className="text-sm">
              ℹ️ Create the shipment first, then you can add products to it.
            </p>
          </div>
        )}
      </div>

      {isAddProductModalOpen && (
        <AddProductModal
          onClose={() => setIsAddProductModalOpen(false)}
          onAdd={handleAddProduct}
        />
      )}

      {addVariationProduct && (
        <AddVariationModal
          restockShipmentProductId={addVariationProduct.id}
          restockShipmentId={addVariationProduct.restockShipmentId}
          supplierProductId={addVariationProduct.supplierProductId}
          onClose={() => setAddVariationProduct(null)}
          onAdd={() => {
            setAddVariationProduct(null);
            refetch();
          }}
        />
      )}

      {isAddBoxModalOpen && (
        <AddCartonBoxModal
          onClose={() => setIsAddBoxModalOpen(false)}
          onAdd={handleAddCartonBox}
        />
      )}

      {linkModalState && shipment && (
        <LinkVariationToBoxModal
          boxId={linkModalState.boxId}
          shipmentProducts={shipment.products}
          cartonBoxes={shipment.cartonBoxes || []}
          onClose={() => setLinkModalState(null)}
          onLink={() => {
            setLinkModalState(null);
            refetch();
          }}
          editingVariation={linkModalState.editingVariation}
          editingLink={linkModalState.editingLink}
        />
      )}

      {isEditMode && activeTab === 'products' && (
        <div className="fixed bottom-8 right-8 z-30">
          <Button preset="primary" onClick={() => setIsAddProductModalOpen(true)}>
            <FaPlus />
            Add Product
          </Button>
        </div>
      )}

      {isEditMode && activeTab === 'boxes' && (
        <div className="fixed bottom-8 right-8 z-30">
          <Button preset="primary" onClick={() => setIsAddBoxModalOpen(true)}>
            <FaPlus />
            Add Carton Box
          </Button>
        </div>
      )}
    </Screen>
  );
};

export default RestockShipmentFormPage;

